export interface WizRuntimeConfig {
  apiPrefix?: string;
  [key: string]: unknown;
}

export interface WizContext {
  appId: string;
  namespace: string;
}

function apiPrefix(): string {
  const configured = window.__APP_CONFIG__?.apiPrefix?.trim() || '/api';
  const leadingSlash = configured.startsWith('/') ? configured : `/${configured}`;
  return leadingSlash === '/' ? '' : leadingSlash.replace(/\/+$/, '');
}

function apiPath(resource: string): string {
  const normalized = String(resource || '').trim();
  const withSlash = normalized.startsWith('/') ? normalized : `/${normalized}`;
  return `${apiPrefix()}${withSlash}`.replace(/\/{2,}/g, '/');
}

async function responseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const contentType = response.headers.get('content-type') || '';
  return contentType.includes('application/json') ? response.json() : response.text();
}

class WizApi {
  public url(resource: string): string {
    return apiPath(resource);
  }

  public eventSource(resource: string): EventSource {
    return new EventSource(this.url(resource), { withCredentials: true });
  }

  private csrfPending?: Promise<{token:string;headerName:string}>;

  private async csrf(): Promise<{token:string;headerName:string}> {
    if (!this.csrfPending) {
      this.csrfPending = fetch(apiPath('/auth/csrf'), { credentials: 'same-origin', cache: 'no-store' })
        .then(async response => {
          const data = await responseBody(response) as any;
          if (!response.ok || !data?.token) throw Object.assign(new Error(data?.message || '보안 토큰을 확인하지 못했습니다.'), {status: response.status, data});
          return data;
        }).finally(() => { this.csrfPending = undefined; });
    }
    return this.csrfPending;
  }

  public async request<T>(method: string, resource: string, body?: unknown, onResponse?: (response: Response) => void): Promise<T> {
    const headers: Record<string,string> = {};
    const mutating = !['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase());
    if (mutating) {
      const csrf = await this.csrf();
      headers[csrf.headerName || 'X-XSRF-TOKEN'] = csrf.token;
      headers['Idempotency-Key'] = crypto.randomUUID();
    }
    const multipart = body instanceof FormData;
    if (body !== undefined && !multipart) headers['content-type'] = 'application/json';
    const response = await fetch(apiPath(resource), {
      method, credentials: 'same-origin', cache: 'no-store', headers,
      body: body === undefined ? undefined : multipart ? body as FormData : JSON.stringify(body)
    });
    const data = await responseBody(response);
    if (!response.ok) {
      if (response.status === 401 && !resource.startsWith('/auth/')) window.dispatchEvent(new Event('infra-session-expired'));
      const error = new Error((data as any)?.message || (data as any)?.detail || `요청 처리 중 오류가 발생했습니다. (${response.status})`);
      Object.assign(error, { status: response.status, data });
      throw error;
    }
    onResponse?.(response);
    return data as T;
  }

  public get<T>(resource: string): Promise<T> {
    return this.request<T>('GET', resource);
  }

  public async getPage<T>(resource: string): Promise<{items: T[]; total: number}> {
    let total = 0;
    const items = await this.request<T[]>('GET', resource, undefined, response => {
      const value = response.headers.get('X-Total-Count');
      if (value === null || !/^\d+$/.test(value) || !Number.isSafeInteger(Number(value)))
        throw new Error('목록의 전체 개수를 확인할 수 없습니다. 다시 시도해 주세요.');
      total = Number(value);
    });
    return {items, total};
  }

  public post<T>(resource: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', resource, body);
  }

  public put<T>(resource: string, body?: unknown): Promise<T> {
    return this.request<T>('PUT', resource, body);
  }

  public patch<T>(resource: string, body?: unknown): Promise<T> {
    return this.request<T>('PATCH', resource, body);
  }

  public delete<T>(resource: string): Promise<T> {
    return this.request<T>('DELETE', resource);
  }
}

export async function loadRuntimeConfig(): Promise<WizRuntimeConfig> {
  const response = await fetch('/app-config.json', { cache: 'no-store', credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Runtime config returned HTTP ${response.status}`);
  const config = await response.json() as WizRuntimeConfig;
  window.__APP_CONFIG__ = config;
  return config;
}

export default class Wiz {
  public readonly api = new WizApi();

  constructor(public readonly context: WizContext) {}
}
